import React, { useContext, useMemo, useState } from 'react';
import { doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { FaIdCard } from 'react-icons/fa';
import TopBar from '../components/TopBar';
import { UserContext } from '../App';
import { db } from '../firebase';
import layoutStyles from '../styles/layout.module.css';
import styles from './Account.module.css';

function handleBack(navigate) {
  if (window.history.length > 2) navigate(-1);
  else navigate('/account');
}

export default function ChangeName() {
  const navigate = useNavigate();
  const appUser = useContext(UserContext);
  const [firstName, setFirstName] = useState(appUser?.firstName || '');
  const [lastName, setLastName] = useState(appUser?.lastName || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const currentName = useMemo(
    () => [appUser?.firstName, appUser?.lastName].filter(Boolean).join(' ').trim() || 'Not set yet',
    [appUser?.firstName, appUser?.lastName]
  );

  const handleSubmit = async (event) => {
    event.preventDefault();
    const nextFirstName = firstName.trim();
    const nextLastName = lastName.trim();

    if (!appUser?.id) {
      setError('You must be signed in to update your name.');
      return;
    }
    if (!nextFirstName || !nextLastName) {
      setError('Enter both a first name and last name.');
      return;
    }

    try {
      setSaving(true);
      setError('');
      setSuccess('');
      await setDoc(doc(db, 'users', appUser.id), {
        firstName: nextFirstName,
        lastName: nextLastName,
        updatedAt: serverTimestamp(),
      }, { merge: true });
      setSuccess('Your name has been updated.');
    } catch (err) {
      setError(err?.message || 'Unable to update your name.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={layoutStyles.detailPage}>
      <TopBar variant="back" backLabel="Back" onBack={() => handleBack(navigate)} rightActionLabel="Home" onRightAction={() => navigate('/home')} />
      <div className={`${layoutStyles.detailMain} ${styles.pageShell}`.trim()}>
        <div className={styles.pageInner}>
          <section className={styles.heroCard}>
            <span className={styles.eyebrow}>Account</span>
            <h1 className={styles.title}>Change Name</h1>
            <p className={styles.subtitle}>Set the fan name that appears across your account, collector profile, and library views.</p>
          </section>

          <section className={styles.sectionCard}>
            <div className={styles.sectionHeader}>
              <div>
                <span className={styles.sectionEyebrow}>Current Name</span>
                <h2 className={styles.sectionTitle}>{currentName}</h2>
              </div>
              <span className={styles.sectionIcon}><FaIdCard /></span>
            </div>

            <form className={styles.form} onSubmit={handleSubmit}>
              <label className={styles.field}>
                <span>First Name</span>
                <input value={firstName} onChange={(event) => setFirstName(event.target.value)} placeholder="First name" autoComplete="given-name" />
              </label>
              <label className={styles.field}>
                <span>Last Name</span>
                <input value={lastName} onChange={(event) => setLastName(event.target.value)} placeholder="Last name" autoComplete="family-name" />
              </label>
              {error ? <p className={styles.errorState}>{error}</p> : null}
              {success ? <p className={styles.successState}>{success}</p> : null}
              <div className={styles.actions}>
                <button type="button" className={styles.secondaryButton} onClick={() => navigate('/account')}>Back to Account</button>
                <button type="submit" className={styles.primaryButton} disabled={saving}>{saving ? 'Saving…' : 'Save Name'}</button>
              </div>
            </form>
          </section>
        </div>
      </div>
    </div>
  );
}
